import moment from 'moment-timezone'
import _ from 'lodash'
import environments from '@adc/core-utils/src/helpers/environments'
import { utils as tkUtils } from '@adc/ui-core'
import { COLUMN_DEFS_PREFIX } from '../constants/cookieKeys'
import { SAVED_SEARCH_BOILER_PLATE } from '../constants/savedSearches'
import { COLUMN_TYPES } from '../constants'
import { renderValue as renderDate } from '../cells/dateCell'
import { DATA_TYPES } from '../searchField'
import {
  convertISOTimeToString,
  API_DATE_FORMAT,
  PACIFIC_TIMEZONE,
  UI_DATE_TIME_FORMAT,
  UI_TIME_FORMAT,
  formatDateApiToUi,
} from './date'
import { formatBool } from './formatters'

import { buildDSL } from './buildDSL'
import {
  filterStateToFilterClauses,
  filterClausesToFilterState,
} from './savedSearchFilters'

const { formatTitleCase } = tkUtils.formatters
const CSV_DATE_FORMAT = 'M/D/YYYY'

const isColumnSortable = (column = {}) => {
  const { colProps = {} } = column
  return colProps.sort >= 0 // if colProps.sort is undefined, the column is not sortable
}

const isColumnSorted = (column = {}) => {
  const { colProps = {} } = column
  return colProps.sort > 0 // if colProps.sort = 0, sort direction is not determined yet
}

const isDataColumn = (column = {}) =>
  ![COLUMN_TYPES.ACTION, COLUMN_TYPES.CHECKBOX].includes(column.type)

const isDateTimeColumn = (column = {}) =>
  [DATA_TYPES.DATETIME, DATA_TYPES.DATETIME_AS_LOCAL].includes(column.dataType)

const getNonDataColumns = (columns = []) =>
  columns.filter(column => !isDataColumn(column))

const getDataColumns = (columns = []) =>
  columns.filter(column => isDataColumn(column))

const getSortedColumns = (columns = []) =>
  columns.filter(column => isColumnSorted(column))

const getSortValuesFromColumns = (columns = []) => {
  const sortValues = []
  const sortedColumns = getSortedColumns(columns)
  sortedColumns.forEach(sortedColumn => {
    const { field, dataType, colProps = {} } = sortedColumn
    sortValues.push({
      field,
      dataType,
      sortAscending: colProps.sortAscending,
    })
  })
  return sortValues
}

const getSearchableColumns = (columns = []) => {
  const isColumnSearchable = (column = {}) => {
    const { colProps = {} } = column
    return colProps.searchable
  }
  return columns
    .filter(column => isColumnSearchable(column))
    .map(column => {
      const newCol = {
        type: column.type,
        text: column.title,
        value: column.field,
        dataType: column.dataType,
        searchType: column.searchType,
        options: column.options,
        operationType: column.operationType,
        operationOptions: column.operationOptions,
        partialMatch: column.partialMatch,
        setMatch: column.setMatch,
        searchAnalyzer: column.searchAnalyzer,
        sortAnalyzer: column.sortAnalyzer,
      }
      return _.pickBy(newCol, val => typeof val !== 'undefined')
    })
}

const resetSortProps = (column = {}, overrideColProps) => {
  const { colProps = {} } = column
  const isSortable = isColumnSortable(column)
  return {
    ...column,
    colProps: {
      ...colProps,
      sort: isSortable ? 0 : undefined,
      sortAscending: undefined,
      ...overrideColProps,
    },
  }
}

const columnPropBaseline = {
  isVisible: true,
  width: 100,
  flexGrow: 0,
}

/*
 * Strip out key/val pairs that we already have in the baseline object...
 *
 * Why? This is to reduce overall browserStorage size for col def info in order
 * to not hit max limits and reduce overall overhead in browser.
 */
const dehydrateColProps = colProps =>
  colProps &&
  _.omitBy(
    colProps,
    (value, key) =>
      undefined !==
      _.find(
        columnPropBaseline,
        (baseValue, baseKey) => value === baseValue && key === baseKey
      )
  )

/*
 *  Add back key/val pairs from baseline object that aren't there already
 */
const hydrateColProps = colProps => ({ ...columnPropBaseline, ...colProps })

/**
 * For storage (e.g. cookie or saved search) the value is saved in the following format (to minimize overhead)
 * [
 *   { template_name: { width: 300, isVisible: true, sort: 1, sortAscending: true } },
 *   { venue_title: { width: 200, isVisible: true } },
 *   { venue_name: { width: 100, isVisible: true, sort: 0 } }
 * ]
 * We store only the visible dataColumns (passed in as memoryColumns param) in an array, as this is the
 * sequence order to be displayed. Non-data columns are ignored, as these are not configurable by users.
 */
const convertColumnsToStorageFormat = dataColumns =>
  dataColumns.reduce((newColumns, column) => {
    const { field, colProps = {} } = column
    if (colProps.isVisible && isDataColumn(column)) {
      const { isResizable, isReorderable, searchable, ...restProps } = colProps // eslint-disable-line no-unused-vars
      newColumns.push({ [field]: { ...restProps } })
    }
    return newColumns
  }, [])

const saveDataColumnsToBrowserStorage = (
  browserStorage,
  entityKey,
  columns = []
) => {
  if (browserStorage) {
    const visibleDataColumns = convertColumnsToStorageFormat(columns).map(
      col => {
        const columnKey = _.keys(col)[0]
        return {
          [columnKey]: dehydrateColProps(col[columnKey]),
        }
      }
    )
    browserStorage.setValue(
      `${COLUMN_DEFS_PREFIX}${entityKey}`,
      visibleDataColumns
    )
  }
}

/**
 * This applies the lighter-weight, storage format for columns (used for browser storage and saved searches), to
 * the columns to update the size, order and sort.
 *
 * Stored column value only has array of visible data columns objects with field name as key and colProps as value
 * (order matters!) For data structure consistency, we will merge back stored data (if available) to the column
 * definition format here. Since the ordering matters, we will treat the stored value as the main reference.
 */
const getColumnsFromStorageFormat = (storedColumns, columns) =>
  storedColumns.reduce((newColumns, storedColumn) => {
    const columnKey = Object.keys(storedColumn)[0]
    const newColumnFound = columns.find(column => column.field === columnKey)
    if (!_.isEmpty(newColumnFound)) {
      // column found in master columns definition MUST RESET its sort properties if defined as sortable
      newColumns.push(resetSortProps(newColumnFound, storedColumn[columnKey]))
    }
    return newColumns
  }, [])

/**
 * Retrieves the columns stored in browserStorage for the entitykey at hand.
 * @param {object} browserStorage
 * @param {string} entityKey
 * @param {array} columns
 */
const getDataColumnsFromBrowserStorage = (
  browserStorage,
  entityKey,
  columns = []
) => {
  // get custom cols from browser storage and hydrate, before we getColumnsFromStorageFormat
  const customColumns =
    (environments.canUseDom() && browserStorage
      ? browserStorage.getValue(`${COLUMN_DEFS_PREFIX}${entityKey}`)
      : null) || []

  const hydratedCustomColumns = customColumns.map(col => {
    const columnKey = Object.keys(col)[0]
    return {
      [columnKey]: hydrateColProps(col[columnKey]),
    }
  })

  const newColumns = getColumnsFromStorageFormat(hydratedCustomColumns, columns)
  return newColumns.length > 0 ? newColumns : undefined
}

const getSelectedSavedSearch = savedSearches => {
  if (_.isEmpty(savedSearches)) return null
  const { selectedSearchId } = savedSearches
  if (_.isNil(selectedSearchId) || !savedSearches.searches) return null

  return _.find(savedSearches.searches, { saved_search_id: selectedSearchId })
}

/**
 * Similar to getDataColumnsFromBrowserStorage, except the col data is pulled out from the currently
 * selected saved search.  Note - we only want cols from saved search when not dirty.
 *
 * @param {object} savedSearches
 * @param {array} columns
 */
const getDataColumnsFromSavedSearch = (savedSearches, columns) => {
  const savedSearch = getSelectedSavedSearch(savedSearches)
  if (_.isNil(savedSearch) || _.isNil(_.get(savedSearch, 'parameters.columns')))
    return null

  const dirtyColumns = savedSearches.dirtyColumns

  if (savedSearches.isDirty && !_.isEmpty(dirtyColumns)) {
    return dirtyColumns
  } else {
    return getColumnsFromStorageFormat(savedSearch.parameters.columns, columns)
  }
}

/**
 * Our storage format for saved searches requires that we stringify the parameters, e.g.
 * the filters and columns, therefore when we retrieve the data, we need to parse those out.
 * This handles just one saved search.
 * @param {object} search
 */
const parseOneSavedSearch = search => ({
  ..._.omit(search, ['parameters']),
  parameters: {
    columns: JSON.parse(search.parameters.columns),
    filters: JSON.parse(search.parameters.filters),
  },
})

/**
 * Our storage format for saved searches requires that we stringify the parameters, e.g.
 * the filters and columns, therefore when we retrieve the data, we need to parse those out.
 * @param {array} savedSearches
 */
const parseSavedSearchData = savedSearches => {
  let parsed = []
  if (_.isArray(savedSearches)) {
    parsed = savedSearches.map(parseOneSavedSearch)
  }
  return parsed
}

const prepSavedSearchParameters = (dirtyColumns, tableInfo, savedSearches) => {
  const columnsToStore = convertColumnsToStorageFormat(dirtyColumns || [])

  // Guard: never persist an empty columns array if the current saved search has columns.
  if (columnsToStore.length === 0) {
    const currentSearch = getSelectedSavedSearch(savedSearches)
    const existingColumns = _.get(currentSearch, 'parameters.columns', [])
    if (existingColumns.length > 0) {
      console.warn(
        '[DataTable] Prevented saving empty columns to saved search.',
        'dirtyColumns was null/empty but saved search has existing columns.',
        {
          searchId: _.get(currentSearch, 'saved_search_id'),
          existingColumnCount: existingColumns.length,
        }
      )
      return {
        columns: JSON.stringify(existingColumns),
        filters: JSON.stringify(tableInfo.filterClauses || []),
      }
    }
  }

  return {
    columns: JSON.stringify(columnsToStore),
    filters: JSON.stringify(tableInfo.filterClauses || []),
  }
}

/**
 * Prepares a payload to be POST'd to the API.
 * @param {object} tableInfo
 * @param {object} savedSearches
 * @param {string} savedSearchName
 * @param {string} tableType
 */
const prepSavedSearchDataForPost = (
  tableInfo,
  savedSearches,
  savedSearchName,
  tableType,
  savedSearchType
) => ({
  ...SAVED_SEARCH_BOILER_PLATE,
  saved_search_name: savedSearchName,
  saved_search_type: savedSearchType,
  namespace: tableType,
  parameters: prepSavedSearchParameters(
    savedSearches.dirtyColumns,
    tableInfo,
    savedSearches
  ),
})

/**
 * Prepares a payload to be PUT'd to the API.
 * @param {object} tableInfo
 * @param {object} savedSearches
 */
const prepSavedSearchDataForPut = (tableInfo, savedSearches) => ({
  ..._.omit(getSelectedSavedSearch(savedSearches), ['parameters']),
  parameters: prepSavedSearchParameters(
    savedSearches.dirtyColumns,
    tableInfo,
    savedSearches
  ),
})

/**
 * Retrieves the column info from storage and filters down to those visible only.
 * @param {object} browserStorage
 * @param {object} savedSearches
 * @param {string} entityKey
 * @param {array} columns (full definitions, not just colProps)
 */
const getVisibleDataColumns = (
  browserStorage,
  savedSearches,
  entityKey,
  columns = []
) => {
  let dataColumns = getDataColumns(columns)
  const savedSearchColumns = getDataColumnsFromSavedSearch(
    savedSearches,
    columns
  )

  if (savedSearchColumns && savedSearchColumns.length > 0) {
    dataColumns = savedSearchColumns
  } else {
    if (savedSearchColumns && savedSearchColumns.length === 0) {
      console.warn(
        '[DataTable] Saved search returned zero columns. Falling back to defaults.',
        { selectedSearchId: _.get(savedSearches, 'selectedSearchId') }
      )
    }
    const customColumns = getDataColumnsFromBrowserStorage(
      browserStorage,
      entityKey,
      columns
    )
    if (customColumns) dataColumns = customColumns
  }

  return dataColumns.filter(column => {
    const { colProps = {} } = column
    return colProps.isVisible
  })
}

const getFieldKeyFromPath = (path = '') => {
  return path.split('.').reverse()[0]
}

const getAllVisibleColumns = (
  browserStorage,
  savedSearches,
  entityKey,
  columns = []
) => {
  const nonDataColumns = getNonDataColumns(columns)
  const visibleDataColumns = getVisibleDataColumns(
    browserStorage,
    savedSearches,
    entityKey,
    columns
  )
  return [...nonDataColumns, ...visibleDataColumns]
}

const getFilterClausesFromSavedSearch = savedSearches => {
  const savedSearch = getSelectedSavedSearch(savedSearches)
  return _.get(savedSearch, 'parameters.filters')
}

const getCellInnerWidth = (colProps = {}) =>
  colProps.flexGrow === 1 ? '100vw' : colProps.width
/**
 *
 * @param {*} columns visible grid columns
 * @param {*} rowData shown on the grid
 *
 * Here we want to return a data structure like this
 * [
 *  [header1, header2, header3],
 *  [row1_field1, row1_field2, row1_field3]
 *  [row2_field1, row2_field2, row2_field3]
 *  ...
 * ]
 */

// TODO: Remove once this feature is not needed (currently needed for listing to venue assignment which is currently being developed to be handled within Morpheus)
const getCSVExportData = (columns, rowData) => {
  if (_.isEmpty(columns) || _.isEmpty(rowData)) {
    return []
  }

  // get data columns that are visible in UI only
  const dataColumns = _.filter(getDataColumns(columns), column =>
    _.get(column, 'colProps.isVisible', false)
  )
  const headers = []
  _.each(dataColumns, column => {
    headers.push(column.title)
    if (isDateTimeColumn(column)) {
      headers.push(column.title.replace('Date', 'Time'))
    }
  })
  const csvData = [headers]

  const getOption = (value, options) => {
    const optionValue = _.find(options, { value })
    return optionValue ? optionValue.text : ''
  }

  const getDate = (value, column) => {
    if (column?.formatOptions) {
      const date = moment(renderDate(value, column.formatOptions))
      return date.isValid() ? date.format(CSV_DATE_FORMAT) : value
    } else {
      return formatDateApiToUi(value, CSV_DATE_FORMAT)
    }
  }
  const getTime = (value, column) => {
    let date
    if (column?.formatOptions) {
      date = moment(renderDate(value, column.formatOptions))
    } else {
      date = moment.utc(value, API_DATE_FORMAT).tz(PACIFIC_TIMEZONE)
    }
    return date.isValid() ? date.format(UI_TIME_FORMAT) : value
  }

  /**
   * Returns a row's field value.
   * Handles special case where the value is referenced as a property in an array of objects,
   * e.g. "roles.role_name" - which is a valid reference from the DSL/ES perspective.
   *
   * Note: this could probably handled better if we had more latitude in terms of specifying
   * an apiRef and what to render (field reference) separately.  If we had that we could just
   * compute a more standard array in translation and reference that. But we can't and I figured
   * it wasn't worth a major change just for this one instance.
   *
   * @param {Array} row
   * @param {String} fieldName
   */
  const getValue = (row, fieldName) => {
    if (!fieldName.includes('.')) return _.get(row, fieldName, '')
    const lastPeriodIndex = fieldName.lastIndexOf('.')
    const firstPart = fieldName.substring(0, lastPeriodIndex)
    const lastPart = fieldName.substring(lastPeriodIndex + 1)
    const val = _.get(row, firstPart, '')

    return Array.isArray(val) && val.length && _.isObject(val[0])
      ? val.map(item => item[lastPart])
      : _.get(row, fieldName, '')
  }

  _.each(rowData, row => {
    const csvRow = []
    _.each(dataColumns, column => {
      const field = getFieldKeyFromPath(column.field)
      const fieldValue = getValue(row, column.field)
      if (isDateTimeColumn(column)) {
        csvRow.push(getDate(fieldValue, column))
        csvRow.push(getTime(fieldValue, column))
      } else if (column.dataType === DATA_TYPES.TIME) {
        const timeParsed = convertISOTimeToString(fieldValue)
        csvRow.push(timeParsed)
      } else if (column.dataType === DATA_TYPES.DATE) {
        // plain date preparation (e.g. MMM DD, YYYY -> M/DD/YYYY)
        let pattern = UI_DATE_TIME_FORMAT
        if (column.formatOptions && column.formatOptions.pattern) {
          pattern = column.formatOptions.pattern
        }
        const dateParsed = moment(fieldValue, pattern)
        if (dateParsed.isValid()) {
          csvRow.push(dateParsed.format(CSV_DATE_FORMAT))
        } else {
          csvRow.push(getDate(fieldValue))
        }
      } else if (column.type === COLUMN_TYPES.BOOL) {
        const boolParsed = formatBool(fieldValue)
        csvRow.push(boolParsed)
      } else if (field === 'venue_type' || field === 'event_type') {
        csvRow.push(formatTitleCase(fieldValue))
      } else if (column.type === COLUMN_TYPES.VALUE_TEXT) {
        const optionParsed = getOption(fieldValue, column.options)
        csvRow.push(optionParsed)
      } else {
        csvRow.push(fieldValue)
      }
    })

    csvData.push(csvRow)
  })

  return csvData
}

const getClauseIndex = (filterClause, filterClauses) =>
  _.findIndex(filterClauses, clause =>
    _.isEqual(clause.fields, filterClause.fields)
  )

export default {
  buildDSL,
  getAllVisibleColumns,
  getCellInnerWidth,
  getDataColumns,
  getNonDataColumns,
  getSearchableColumns,
  getSortedColumns,
  getSortValuesFromColumns,
  getVisibleDataColumns,
  isColumnSortable,
  isColumnSorted,
  resetSortProps,
  saveDataColumnsToBrowserStorage,
  parseOneSavedSearch,
  parseSavedSearchData,
  prepSavedSearchDataForPost,
  prepSavedSearchDataForPut,
  getCSVExportData,
  getFieldKeyFromPath,
  getFilterClausesFromSavedSearch,
  getDataColumnsFromSavedSearch,
  getClauseIndex,
  dehydrateColProps,
  hydrateColProps,
  filterStateToFilterClauses,
  filterClausesToFilterState,
}
